import React, { createContext, FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from "react";

import PageToast, { PageToastTone } from "../../../components/shared/page_toast";

export type ShowToastOptions = {
  tone?: PageToastTone
  iconName?: string
  /** 0 keeps the toast up until it is dismissed. */
  dismissAfterMs?: number
};

export type ToastApi = {
  show: (message: string, options?: ShowToastOptions) => void
  dismiss: () => void
};

type Toast = {
  message: string
  tone: PageToastTone
  iconName: string
  dismissAfterMs: number
};

const ToastContext = createContext<ToastApi | null>(null);

/**
 * The app's Nova toast: the small confirmation that appears bottom-right, per the Figma for HMA-1546.
 * Any feature can raise one without owning state, a timer, or markup:
 *
 *   const { show } = useToast();
 *   show("Updated BIM version date", { tone: "success" });
 *
 * This is deliberately separate from the app-wide Redux `toastNotification` banner rendered by
 * SnackbarNotification / ToastNotification, which is the older top-of-page style. A page that raises
 * one of these while a Redux action also fires the banner should keep dispatching
 * toastNotificationExpired() to suppress the duplicate, until that renderer is replaced by this one.
 */
export const useToast = (): ToastApi => {
  const api = useContext(ToastContext);
  if (api == null) {
    throw new Error("useToast was called outside a ToastProvider. The provider is mounted in application.tsx, and in renderWithStore for tests.");
  }
  return api;
};

export const ToastProvider: FunctionComponent = ({ children }) => {
  const [toast, setToast] = useState<Toast | null>(null);

  const show = useCallback((message: string, options: ShowToastOptions = {}) => {
    setToast({
      message,
      tone: options.tone ?? "neutral",
      iconName: options.iconName ?? "check_circle",
      dismissAfterMs: options.dismissAfterMs ?? 4000
    });
  }, []);

  const dismiss = useCallback(() => setToast(null), []);

  // Keyed on the toast object rather than its message, so raising the same message twice restarts the
  // countdown instead of letting the first one's timer close the second.
  useEffect(() => {
    if (!toast?.dismissAfterMs) {
      return undefined;
    }
    const timer = window.setTimeout(() => setToast(null), toast.dismissAfterMs);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return <ToastContext.Provider value={api}>
    {children}
    {toast ? <PageToast
      message={toast.message}
      tone={toast.tone}
      iconName={toast.iconName}
      onDismiss={dismiss}
    /> : null}
  </ToastContext.Provider>;
};

export default useToast;
